"use client";

import { Play } from "lucide-react";
import { cdnUrl } from "@/lib/cdn";
import type { Photo } from "@/lib/types/event";
import { photoCount } from "@/components/event/labels";
import { openGallery, type GalleryOptions } from "@/components/event/EventGallery";

/**
 * Up to `max` square photos in a row, each opening the whole set in the
 * gallery at that photo. The last is darkened and says how many there are in
 * all. A video among them (galleryMedia) has a play button over it. The stops
 * of a programme and a trail's popup use it.
 */
export function PhotoStrip({
  photos,
  max = 4,
  className = "",
  gallery,
  onOpen,
}: {
  photos: Photo[] | null | undefined;
  max?: number;
  className?: string;
  gallery?: GalleryOptions;
  /** called as a tile is pressed, before the gallery opens */
  onOpen?: (index: number) => void;
}) {
  const list = photos || [];
  const shown = list.slice(0, max);
  if (shown.length === 0) {
    return null;
  }
  return (
    <div className={`flex gap-1.5 ${className}`}>
      {shown.map((photo, i) => {
        const last = i === shown.length - 1;
        // a video alone keeps its play button rather than the count
        const counted = last && !(photo.video && list.length === 1);
        return (
          <button
            key={photo.id}
            type="button"
            className="pressable relative aspect-square min-w-0 flex-1 overflow-hidden rounded-[10px] bg-line bg-cover bg-center"
            style={{ backgroundImage: `url('${cdnUrl(photo.thumbnail || photo.path)}')` }}
            aria-label={photo.video ? `Vídeo ${i + 1}` : `Fotografia ${i + 1}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(i);
              openGallery(list, i, gallery);
            }}
          >
            {!!photo.video && !counted && (
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm">
                  <Play size={15} fill="currentColor" className="translate-x-px" />
                </span>
              </span>
            )}
            {counted && (
              <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-[13px] font-bold text-white">{photoCount(list.length)}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
